import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { invitationService } from '../services/invitationService';
import { Button, Label, Input, ErrorMessage, Card } from '../styles/GlobalStyles';
import { LoadingSpinner } from '../components/LoadingSpinner';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--background-primary);
  padding: var(--spacing-md);
`;

const InvitationCard = styled(Card)`
  max-width: 500px;
  width: 100%;
`;

const PageTitle = styled.h1`
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  text-align: center;
`;

const InvitationDetails = styled.div`
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--spacing-sm);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.875rem;
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const PasswordRequirements = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-sm);
  margin-top: var(--spacing-xs);
  font-size: 0.75rem;
  color: #0c4a6e;
  
  ul {
    margin: var(--spacing-xs) 0 0 0;
    padding-left: var(--spacing-md);
  }
  
  li {
    margin-bottom: 2px;
  }
`;

const ErrorCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const ErrorTitle = styled.h2`
  color: var(--error-color);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: var(--spacing-md);
`;

const ErrorText = styled.p`
  color: var(--text-secondary);
  margin-bottom: var(--spacing-lg);
`;

interface AcceptInvitationFormData {
  password: string;
  confirmPassword: string;
}

export const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [serverError, setServerError] = useState<string>('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptInvitationFormData>();

  const password = watch('password');

  useEffect(() => {
    if (token) {
      loadInvitationDetails();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInvitationDetails = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      const details = await invitationService.getInvitationDetails(token);
      
      // Check if invitation is expired or already accepted
      if (details.status === 'EXPIRED') {
        setError('This invitation has expired. Please contact the team owner for a new invitation.');
      } else if (details.status === 'ACCEPTED') {
        setError('This invitation has already been accepted. Please log in with your credentials.');
      } else {
        setInvitationDetails(details);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to load invitation details';
      const errorCode = error.response?.data?.error?.code;
      
      if (errorCode === 'INVITATION_NOT_FOUND' || error.response?.status === 404) {
        setError('This invitation is invalid or does not exist.');
      } else if (errorCode === 'INVITATION_EXPIRED') {
        setError('This invitation has expired. Please contact the team owner for a new invitation.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = (value: string) => {
    if (value.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(value)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(value)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(value)) {
      return 'Password must contain at least one number';
    }
    return true;
  };

  const onSubmit = async (data: AcceptInvitationFormData) => {
    if (!token) return;

    setIsSubmitting(true);
    setServerError('');

    try {
      const response = await invitationService.acceptInvitation(token, {
        password: data.password,
      });

      // Store token and redirect to dashboard
      localStorage.setItem('token', response.token);
      localStorage.setItem('currentUserEmail', response.user.email);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to accept invitation';
      const errorCode = error.response?.data?.error?.code;
      
      if (errorCode === 'INVITATION_EXPIRED') {
        setServerError('This invitation has expired. Please contact the team owner for a new invitation.');
      } else if (errorCode === 'INVITATION_ALREADY_USED') {
        setServerError('This invitation has already been used. Please log in with your credentials.');
      } else if (errorCode === 'INVALID_TOKEN') {
        setServerError('This invitation is invalid. Please check the link and try again.');
      } else {
        setServerError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorCard>
          <ErrorTitle>Invitation Error</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </ErrorCard>
      </PageContainer>
    );
  }

  if (!invitationDetails) {
    return (
      <PageContainer>
        <ErrorCard>
          <ErrorTitle>Invalid Invitation</ErrorTitle>
          <ErrorText>This invitation could not be found.</ErrorText>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </ErrorCard>
      </PageContainer>
    );
  }

  const expirationDate = new Date(invitationDetails.expiresAt);

  return (
    <PageContainer>
      <InvitationCard>
        <PageTitle>Accept Team Invitation</PageTitle>

        <InvitationDetails>
          <DetailRow>
            <DetailLabel>Team:</DetailLabel>
            <DetailValue>{invitationDetails.teamName}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Invited by:</DetailLabel>
            <DetailValue>
              {invitationDetails.invitedBy.firstName} {invitationDetails.invitedBy.lastName}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Your name:</DetailLabel>
            <DetailValue>
              {invitationDetails.invitedFirstName} {invitationDetails.invitedLastName}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Email:</DetailLabel>
            <DetailValue>{invitationDetails.invitedEmail}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Expires:</DetailLabel>
            <DetailValue>{expirationDate.toLocaleDateString()}</DetailValue>
          </DetailRow>
        </InvitationDetails>

        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="password">Create Password *</Label>
            <Input
              id="password"
              type="password"
              {...register('password', {
                required: 'Password is required',
                validate: validatePassword,
              })}
            />
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
            <PasswordRequirements>
              <strong>Password Requirements:</strong>
              <ul>
                <li>At least 8 characters long</li>
                <li>Contains at least one uppercase letter</li>
                <li>Contains at least one lowercase letter</li>
                <li>Contains at least one number</li>
              </ul>
            </PasswordRequirements>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password *</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === password || 'Passwords do not match',
              })}
            />
            {errors.confirmPassword && (
              <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
            )}
          </FormGroup>

          {serverError && <ErrorMessage>{serverError}</ErrorMessage>}

          <Button type="submit" disabled={isSubmitting} style={{ width: '100%' }}>
            {isSubmitting ? 'Accepting...' : 'Accept Invitation & Create Account'}
          </Button>
        </form>
      </InvitationCard>
    </PageContainer>
  );
};
